<script setup lang="ts">
const {
  min = 0,
  max = 100,
  pushOpposite = false,
} = defineProps<{
  min?: number
  max?: number
  pushOpposite?: boolean
}>()

const emit = defineEmits<{
  (e: 'change' | 'input', min: number, max: number): void
}>()

const minValue = defineModel<number>('minValue', { default: 0 })
const maxValue = defineModel<number>('maxValue', { default: 100 })

const inputMin = ref<HTMLInputElement | null>(null)
const inputMax = ref<HTMLInputElement | null>(null)

const updateMin = (event: Event) => {
  const value = parseInt((event.target as HTMLInputElement).value)

  minValue.value = value

  if (value > maxValue.value) {
    if (pushOpposite) {
      maxValue.value = minValue.value
    }
    else {
      minValue.value = maxValue.value
    }
  }

  if (inputMin.value) {
    inputMin.value.value = String(minValue.value)
  }
}

const updateMax = (event: Event) => {
  const value = parseInt((event.target as HTMLInputElement).value)

  maxValue.value = value

  if (value < minValue.value) {
    if (pushOpposite) {
      minValue.value = maxValue.value
    }
    else {
      maxValue.value = minValue.value
    }
  }

  if (inputMax.value) {
    inputMax.value.value = String(maxValue.value)
  }
}

const emitChange = () => {
  emit('change', minValue.value, maxValue.value)
  emit('input', minValue.value, maxValue.value)
}
</script>

<template>
  <div
    :style="{ '--min-value': Math.max(minValue, min), '--max-value': Math.min(maxValue, max), '--min': min, '--max': max }"
    role="group"
    aria-label="Range Slider"
    class="range-slider"
  >
    <label
      for="range-slider__min"
      class="range-slider__label"
    >Range slider min value</label>
    <input
      id="range-slider__min"
      ref="inputMin"
      :value="minValue || min"
      :min="min"
      :max="max"
      type="range"
      class="range-slider__input range-slider__input--min"
      @input="updateMin($event)"
      @change="emitChange"
      @blur="emitChange"
    >
    <output
      for="range-slider__min"
      class="range-slider__output range-slider__output--min"
    >{{ minValue || min }}</output>

    <label
      for="range-slider__max"
      class="range-slider__label"
    >Range slider max value</label>
    <input
      id="range-slider__max"
      ref="inputMax"
      :value="maxValue || max"
      :min="min"
      :max="max"
      type="range"
      class="range-slider__input range-slider__input--max"
      @input="updateMax($event)"
      @change="emitChange"
      @blur="emitChange"
    >
    <output
      for="range-slider__max"
      class="range-slider__output range-slider__output--max"
    >{{ maxValue || max }}</output>
  </div>
</template>

<style>
@import url('./MinMaxRange.css');
</style>
